// src/pages/MainPanel/CreateExperiment/components/DatasetSelector.jsx
import { Box, TextField, MenuItem, Chip, Link } from "@mui/material";
import { useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { INK_MUTED } from "../../../../theme/tokens";

/**
 * Reusable multi-select dataset picker.
 *
 * Controlled: parent owns `selectedDatasets` (array of dataset objects).
 * Calls `onSelectedDatasetsChange` with the new array whenever selection changes.
 */
const DatasetSelector = ({
  availableDatasets,
  selectedDatasets,
  onSelectedDatasetsChange,
  disabled,
  onGoToDatasets,
}) => {
  const selectedNames = useMemo(
    () => selectedDatasets.map((d) => d.id),
    [selectedDatasets],
  );

  const namesToDatasetObjects = useCallback(
    (names) => {
      const nameSet = new Set(names);
      return availableDatasets.filter((d) => nameSet.has(d.id));
    },
    [availableDatasets],
  );

  const handleChange = (e) => {
    const value = e.target.value;
    const names = typeof value === "string" ? value.split(",") : value;
    onSelectedDatasetsChange(namesToDatasetObjects(names));
  };

  const handleRemove = (datasetName) => {
    onSelectedDatasetsChange(
      selectedDatasets.filter((d) => d.id !== datasetName),
    );
  };

  return (
    <TextField
      select
      fullWidth
      value={selectedNames}
      onChange={handleChange}
      disabled={disabled}
      sx={{ mb: 1 }}
      slotProps={{
        select: {
          multiple: true,
          displayEmpty: true,
          renderValue: (selected) =>
            selected.length === 0 ? (
              <Box component="span" sx={{ color: INK_MUTED }}>
                Select one or more datasets
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((name) => (
                  <Chip
                    key={name}
                    label={
                      availableDatasets.find((d) => d.id === name)?.title ||
                      name
                    }
                    size="small"
                    onMouseDown={(e) => e.stopPropagation()}
                    onDelete={() => handleRemove(name)}
                  />
                ))}
              </Box>
            ),
          MenuProps: {
            PaperProps: {
              style: { maxHeight: 360, overflowY: "auto" },
            },
          },
        },
      }}
      helperText={
        <>
          Detailed dataset descriptions are available{" "}
          <Link
            component="button"
            onClick={onGoToDatasets}
            sx={{ fontSize: "inherit", p: 0, minWidth: 0, textAlign: "left" }}
          >
            here
          </Link>
          .
        </>
      }
    >
      <MenuItem value="" disabled>
        <em>Select one or more datasets…</em>
      </MenuItem>
      {availableDatasets.map((d) => (
        <MenuItem key={d.id} value={d.id}>
          {d.title}
        </MenuItem>
      ))}
    </TextField>
  );
};

DatasetSelector.propTypes = {
  availableDatasets: PropTypes.array.isRequired,
  selectedDatasets: PropTypes.array.isRequired,
  onSelectedDatasetsChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  onGoToDatasets: PropTypes.func,
};

export default DatasetSelector;
